import {
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  Chip,
  Theme,
  Stack,
  Checkbox,
} from '@mui/material';
import { ChangeEvent, FC } from 'react';
import FormControl from '../form-control';
import { FormProps, getMuiSize } from '../helpers';
import { createUseStyles } from 'react-jss';
import { UiOption } from '@/lib/helpers';
import Text from '@/components/text';

interface Props extends FormProps {
  value: string[];
  options: UiOption[];
  light?: boolean;
  onChange: (options: UiOption[]) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  input: {
    backgroundColor: theme.palette.common.white,
    '& fieldset': {
      borderWidth: '1px !important',
    },
    '&.Mui-disabled fieldset': {
      borderColor: `${theme.palette.darkGray} !important`,
    },
    '& .MuiFormLabel-filled .MuiSelect-select': {
      paddingBottom: '15.5px',
    },
  },
  option: {
    '& span.MuiCheckbox-root': {
      padding: '4px 4px 4px 0px',
    },
  },
  selectedChip: {
    overflow: 'hidden',
  },
}));

const SelectMulti: FC<Props> = ({
  name,
  value = [''],
  options,
  label = '',
  disabled = false,
  autoFocus = false,
  onChange,
  fullWidth,
  error = '',
  size = 'medium',
  light = false,
  readonly = false,
  required = false,
}) => {
  const styles = useStyles();
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // convert the heinous return value of the MUI Select ([any,any,any..., string]) into UiOption[]
    const values = (event.target.value || []) as (UiOption | string)[];
    const selected: UiOption[] = [];
    let newValue = '';

    values.forEach((opt) => {
      if (typeof opt === 'string') {
        newValue = opt;
        return;
      }
      selected.push(opt);
    });

    const existIndex = selected.findIndex(({ value }) => value === newValue);
    if (existIndex === -1) {
      const newOption = options.find(({ value }) => value === newValue);
      if (newOption) {
        selected.push(newOption);
      }
    } else {
      selected.splice(existIndex, 1);
    }
    onChange(selected);
  };

  const labelId = `${name}-label`;
  const labelFormatted = required && typeof label === 'string' ? `${label} *` : label;
  const valueOpts = options.filter((opt) => value.includes(opt.value));

  return (
    <FormControl label={labelFormatted} error={error} name={name} size={size} light={light}>
      <MuiSelect
        labelId={labelId}
        label={labelFormatted}
        id={name}
        name={name}
        multiple
        disabled={disabled || readonly}
        autoFocus={autoFocus}
        value={valueOpts}
        onChange={handleChange}
        input={<OutlinedInput id="select-multiple-chip" label={label} className={styles.input} />}
        renderValue={(selected: UiOption[]) => {
          return (
            <Stack direction="row" gap={0.5} height={23} className={styles.selectedChip}>
              {selected.map(({ label, value }) => (
                <Chip key={value} label={label} size="small" />
              ))}
            </Stack>
          );
        }}
        fullWidth={fullWidth}
        error={!!error}
        size={getMuiSize(size)}
      >
        {options.map(({ label, value: optValue, disabled = false }) => {
          return (
            <MenuItem key={optValue} value={optValue} disabled={disabled} className={styles.option}>
              <Stack direction="row" gap={1} alignItems="center">
                <Checkbox checked={value.includes(optValue)} disableRipple />
                <Text>{label}</Text>
              </Stack>
            </MenuItem>
          );
        })}
      </MuiSelect>
    </FormControl>
  );
};

export default SelectMulti;
